import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { toast } from "sonner";
import { ChevronsUpDown, Check, AlertTriangle, Loader2 } from "lucide-react";

const PAGE_SIZE = 50;

// Município — Ajuste de Produto: NUNCA mais campo livre de 7 dígitos.
// Autocomplete/combobox pesquisável sobre a lista oficial de municípios
// do IBGE (ver backend/integrations/nfse_nacional/municipios.py e
// docs/NFSE_NACIONAL.md). Busca sempre no BACKEND (o backend também
// valida na hora de salvar — nunca confia só no frontend). Componente
// REUTILIZÁVEL — mesmo padrão de CodigoTributacaoNacionalCombobox.jsx,
// incluindo a mesma correção de paginação/quebra de linha (Editor completo,
// itens 5/6).
export default function MunicipioCombobox({ value, info, onChange, testId = "nfse-municipio" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const debounceRef = useRef(null);

  const search = async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/config/fiscal/nfse/municipios", { params: { q, limit: PAGE_SIZE, offset: 0 } });
      setResults(data.resultados || []);
      setTotal(data.total ?? (data.resultados || []).length);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/config/fiscal/nfse/municipios", { params: { q: query, limit: PAGE_SIZE, offset: results.length } });
      setResults((prev) => [...prev, ...(data.resultados || [])]);
      setTotal(data.total ?? total);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open]);

  useEffect(() => {
    if (open && results.length === 0 && !query) search("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const currentLabel = value
    ? (info?.nome ? `${info.nome} — ${info.uf} • IBGE ${value}` : `IBGE ${value}`)
    : "Selecione o município...";
  const restantes = Math.max(total - results.length, 0);

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full h-auto min-h-9 justify-between font-normal text-left py-2"
            data-testid={testId}
          >
            <span className="min-w-0 flex-1 whitespace-normal break-words leading-snug" title={currentLabel}>{currentLabel}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[380px] max-w-[92vw] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Buscar por cidade, UF ou código IBGE..."
              value={query}
              onValueChange={setQuery}
              data-testid={`${testId}-busca`}
            />
            <CommandList>
              <CommandEmpty>{loading ? "Buscando..." : "Nenhum município encontrado."}</CommandEmpty>
              <CommandGroup>
                {results.map((r) => (
                  <CommandItem
                    key={r.codigo_ibge}
                    value={r.codigo_ibge}
                    onSelect={() => { onChange(r); setOpen(false); setQuery(""); }}
                    data-testid={`${testId}-opcao-${r.codigo_ibge}`}
                    className="items-start"
                  >
                    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${value === r.codigo_ibge ? "opacity-100" : "opacity-0"}`} />
                    <span className="min-w-0 flex-1 whitespace-normal break-words leading-snug" title={r.exibicao}>{r.exibicao}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
            {restantes > 0 && (
              <div className="border-t p-1.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs"
                  disabled={loadingMore}
                  onClick={loadMore}
                  data-testid={`${testId}-carregar-mais`}
                >
                  {loadingMore ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : null}
                  Carregar mais ({restantes} de {total} restantes)
                </Button>
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>
      {info && info.valido === false && (
        <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
          <AlertTriangle size={12} />
          Este código não consta na lista oficial do IBGE — a configuração foi preservada, mas selecione um
          município válido antes de emitir.
        </p>
      )}
    </div>
  );
}
